import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, Info, Calendar } from "lucide-react";

const QuoteResults = ({ data, leadData }) => {
  const navigate = useNavigate();

  const handleBookCall = () => {
    // Calendly popup with pre-filled data and UTM parameters
    if (window.Calendly) {
      const prefillData = {};
      if (leadData?.name) {
        prefillData.name = leadData.name;
      }
      if (leadData?.email) {
        prefillData.email = leadData.email;
      }

      const calendlyUrl = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/adrianj901/30min';
      
      window.Calendly.initPopupWidget({
        url: calendlyUrl,
        prefill: prefillData,
        utm: {
          utmCampaign: 'insurance_quote',
          utmSource: 'insuresmart',
          utmMedium: 'quote_results',
          utmContent: 'book_call_button'
        }
      });
    }
    return false;
  };

  const policies = [
    {
      name: "10-Year Term Life",
      coverage: "$100,000",
      premium: data.term_10,
      description: "Affordable coverage for a decade",
      popular: false,
    },
    {
      name: "20-Year Term Life",
      coverage: "$100,000",
      premium: data.term_20,
      description: "Most popular choice for families",
      popular: true,
    },
    {
      name: "30-Year Term Life",
      coverage: "$100,000",
      premium: data.term_30,
      description: "Maximum term protection",
      popular: false,
    },
    {
      name: "Whole Life",
      coverage: "$100,000",
      premium: data.whole_life,
      description: "Lifetime coverage with cash value",
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F5F5F5, #FFFFFF, #FAFAFA)' }}>
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/financial-flight-logo.png" 
              alt="Financial Flight" 
              className="w-10 h-10"
            />
            <span className="text-xl font-bold" style={{ color: '#2E2E2E' }}>Financial Flight</span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 transition-opacity"
            data-testid="back-home-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Start New Quote
          </button>
        </div>
      </header>

      {/* Main Content */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        {/* Success Message */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <CheckCircle2 className="w-8 h-8" style={{ color: '#D4AF37' }} />
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2E2E2E' }}>Your Personalized Quotes</h1>
            <p className="text-sm text-gray-600">Based on current market averages</p>
          </div>
        </div>

        {/* Quote Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {policies.map((policy, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl border p-6 shadow-lg transition-all hover:shadow-xl ${
                policy.popular ? "ring-2 ring-opacity-50" : "border-gray-200"
              }`}
              style={policy.popular ? { borderColor: '#D4AF37', ringColor: '#D4AF37' } : {}}
              data-testid={`quote-card-${index}`}
            >
              {policy.popular && (
                <div className="absolute -top-3 left-6 px-3 py-1 text-white text-xs font-semibold rounded-full" style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}>
                  Most Popular
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#2E2E2E' }} data-testid={`policy-name-${index}`}>
                    {policy.name}
                  </h3>
                  <p className="text-sm text-gray-600">{policy.description}</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold" style={{ color: '#D4AF37' }} data-testid={`policy-premium-${index}`}>
                    ${policy.premium.toFixed(2)}
                  </span>
                  <span className="text-gray-600">/month</span>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Coverage Amount</span>
                    <span className="font-semibold text-gray-900" data-testid={`policy-coverage-${index}`}>
                      {policy.coverage}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleBookCall}
                  className="w-full h-11 transition-all hover:opacity-90"
                  style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
                  data-testid={`apply-button-${index}`}
                >
                  Get This Coverage
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900 text-sm">Important Information</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                These are ballpark estimates based on recent average rates from sources including Progressive, 
                Guardian Life, Aflac, and Policygenius. Actual premiums may vary based on your health, lifestyle, 
                and the specific insurance provider. Final rates are determined after medical underwriting.
              </p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mt-8 text-center">
          <p className="text-gray-700 mb-1">Ready to move forward?</p>
          <p className="text-gray-600 text-sm mb-4">Need more coverage?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              variant="outline"
              className="h-11 hover:opacity-80"
              style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
              onClick={() => navigate("/")}
              data-testid="get-another-quote-button"
            >
              Get Another Quote
            </Button>
            <Button
              className="h-11 flex items-center gap-2 transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
              onClick={handleBookCall}
              data-testid="book-call-button"
            >
              <Calendar className="w-4 h-4" />
              Book a Call
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteResults;